import { gql } from '@apollo/client';

import { SHORT_LINK_FIELDS } from '../fragments/shortLinks';

/**
 * ТЗ 0080 — экран «Короткие ссылки».
 *
 * Имена операций намеренно НЕ совпадают с полями бэкенда (`shortLinks` →
 * `ShortLinkList` и т. д.): совпадение имени операции и поля схлопывает тип
 * результата в кодгене (скилл graphql-codegen).
 */

/**
 * Настроен ли сократитель на сервере (`SHORTLINK_BASE_URL`). Нет — кнопка
 * «Создать ссылку» выключена с пояснением, а не отказывает после ввода.
 */
export const ShortLinkSettingsInfoQuery = gql`
  query ShortLinkSettingsInfo {
    shortLinkSettings {
      enabled
      baseUrl
    }
  }
`;

/**
 * Список и сводка — два поля с ОДНИМ набором фильтров в одной операции:
 * «найдено 120», плитки «действуют / переходов» и таблица обязаны отвечать на
 * один и тот же вопрос, и за один запрос. Счётчик — `shortLinkSummary.total`
 * (отдельный `shortLinksTotal` посчитал бы то же самое второй раз).
 */
export const ShortLinkListQuery = gql`
  query ShortLinkList(
    $search: String
    $kind: String
    $state: String
    $mine: Boolean
    $channel: String
    $tag: String
    $createdById: ID
    $createdFrom: Date
    $createdTo: Date
    $clicks: String
    $orderBy: String
    $limit: Int
    $offset: Int
  ) {
    shortLinks(
      search: $search
      kind: $kind
      state: $state
      mine: $mine
      channel: $channel
      tag: $tag
      createdById: $createdById
      createdFrom: $createdFrom
      createdTo: $createdTo
      clicks: $clicks
      orderBy: $orderBy
      limit: $limit
      offset: $offset
    ) {
      ...ShortLinkFields
    }
    shortLinkSummary(
      search: $search
      kind: $kind
      state: $state
      mine: $mine
      channel: $channel
      tag: $tag
      createdById: $createdById
      createdFrom: $createdFrom
      createdTo: $createdTo
      clicks: $clicks
    ) {
      total
      live
      humanClicks
      clicks
    }
  }
  ${SHORT_LINK_FIELDS}
`;

/**
 * Страница выгрузки CSV: те же фильтры и порядок, что у списка, но без
 * счётчика и сводки — выгрузка листает страницами по 100 и считать сводку на
 * каждой странице незачем.
 */
export const ShortLinkExportPageQuery = gql`
  query ShortLinkExportPage(
    $search: String
    $kind: String
    $state: String
    $mine: Boolean
    $channel: String
    $tag: String
    $createdById: ID
    $createdFrom: Date
    $createdTo: Date
    $clicks: String
    $orderBy: String
    $limit: Int
    $offset: Int
  ) {
    shortLinks(
      search: $search
      kind: $kind
      state: $state
      mine: $mine
      channel: $channel
      tag: $tag
      createdById: $createdById
      createdFrom: $createdFrom
      createdTo: $createdTo
      clicks: $clicks
      orderBy: $orderBy
      limit: $limit
      offset: $offset
    ) {
      ...ShortLinkFields
    }
  }
  ${SHORT_LINK_FIELDS}
`;

/**
 * Что уже встречается в ссылках юрлица: типы, метки, авторы — варианты
 * фильтров и подсказки в форме. Справочник целиком, а не из отфильтрованной
 * выдачи: иначе после выбора список схлопывался бы до выбранного.
 */
export const ShortLinkFacetListQuery = gql`
  query ShortLinkFacetList {
    shortLinkFacets {
      channels
      tags
      authors {
        id
        name
      }
    }
  }
`;

/** Карточка одной ссылки. */
export const ShortLinkCardQuery = gql`
  query ShortLinkCard($id: ID!) {
    shortLink(id: $id) {
      ...ShortLinkFields
    }
  }
  ${SHORT_LINK_FIELDS}
`;

/**
 * Переходы по дням (Варшава, с нулями) — график карточки: люди и все.
 */
export const ShortLinkDailyClickListQuery = gql`
  query ShortLinkDailyClickList($id: ID!, $days: Int) {
    shortLinkDailyClicks(id: $id, days: $days) {
      date
      human
      total
    }
  }
`;

/**
 * Последние переходы по ссылке: без IP и без полного User-Agent — экрану
 * хватает «человек / робот / повтор» и домена, откуда пришли.
 */
export const ShortLinkClickListQuery = gql`
  query ShortLinkClickList($id: ID!, $limit: Int) {
    shortLinkClicks(id: $id, limit: $limit) {
      id
      clickedAt
      human
      counted
      isBot
      source
    }
  }
`;
